import { Link } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  Layers,
  Wallet,
  ShieldCheck,
  UserCheck,
  Plug,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Hero } from "@/components/marketing/Hero";
import { SectionContainer } from "@/components/marketing/SectionContainer";
import { CtaBand } from "@/components/marketing/CtaBand";
import { AgentMarqueeCarousel } from "@/components/marketing/AgentMarqueeCarousel";
import { PricingCard } from "@/components/marketing/PricingCard";
import { TestimonialCard } from "@/components/marketing/TestimonialCard";
import { FaqItem } from "@/components/marketing/FaqItem";
import { PRICING_TIERS } from "@/app/marketing/constants/pricing";
import { HOME_FAQ } from "@/app/marketing/constants/faq";
import { usePageMeta } from "./usePageMeta";

export function Home() {
  usePageMeta({
    title: "Hire your next teammate — VirtualDesk.ai",
    description:
      "VirtualDesk.ai is a one-stop hiring platform for AI-powered virtual employees. Engineers, executive assistants, customer support, SDRs, and more — configured, audit-logged, and working in minutes.",
  });
  return (
    <>
      <Hero />
      <ValueProps />
      <HowItWorksStrip />
      <AgentCatalog />
      <FeatureDeepDive />
      <PricingPreview />
      <Testimonials />
      <Faq />
      <CtaBand />
    </>
  );
}

function ValueProps() {
  const items = [
    {
      icon: Clock,
      title: "Hire in minutes, not months",
      body: "Pick a role, set the boundaries, connect the tools. Your first agent is working before your next standup.",
    },
    {
      icon: Layers,
      title: "Train once, scale forever",
      body: "Configure an agent's prompt, guardrails, and approvals once. Clone them across workspaces or customers in one click.",
    },
    {
      icon: Wallet,
      title: "Pay per agent, not per hour",
      body: "Flat monthly pricing per agent. No per-token invoices, no surprise overage. Runtime caps protect you from runaway loops.",
    },
  ];
  return (
    <SectionContainer className="border-t border-white/5">
      <div className="grid md:grid-cols-3 gap-6 md:gap-8">
        {items.map((it) => (
          <div key={it.title}>
            <div className="inline-flex rounded-lg bg-brand-violet/15 p-2.5 text-brand-violet">
              <it.icon className="h-6 w-6" />
            </div>
            <h3 className="mt-4 font-display text-2xl md:text-3xl tracking-wide text-brand-ink">
              {it.title}
            </h3>
            <p className="mt-2 text-base text-brand-ink/70 leading-relaxed">{it.body}</p>
          </div>
        ))}
      </div>
    </SectionContainer>
  );
}

function HowItWorksStrip() {
  const steps = [
    {
      num: "01",
      title: "Create your workspace",
      body: "Sign up, pick a subdomain. Your team URL is live in 30 seconds.",
    },
    {
      num: "02",
      title: "Hire agents from the catalog",
      body: "Browse a dozen virtual roles. Click, configure, done.",
    },
    {
      num: "03",
      title: "Connect your tools",
      body: "OAuth into GitHub, Slack, Gmail, Calendar, Twilio — scoped permissions only.",
    },
    {
      num: "04",
      title: "They start working",
      body: "Every action is audit-logged. High-impact work gates behind your approval.",
    },
  ];
  return (
    <SectionContainer className="border-t border-white/5 bg-white/[0.02]">
      <div className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">
          How it works
        </p>
        <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
          From signup to shipping, four steps.
        </h2>
      </div>

      <ol className="mt-12 grid md:grid-cols-4 gap-6 md:gap-4">
        {steps.map((s) => (
          <li
            key={s.num}
            className="relative rounded-xl border border-white/10 bg-white/5 p-5"
          >
            <span className="font-mono text-xs font-bold text-brand-violet">{s.num}</span>
            <h3 className="mt-2 text-lg font-semibold text-brand-ink">{s.title}</h3>
            <p className="mt-2 text-sm text-brand-ink/65 leading-relaxed">{s.body}</p>
          </li>
        ))}
      </ol>

      <div className="mt-10">
        <Button asChild variant="outline" className="border-white/20 text-brand-ink hover:bg-white/5">
          <Link to="/how-it-works">
            See the full walkthrough
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </SectionContainer>
  );
}

function AgentCatalog() {
  return (
    <SectionContainer className="border-t border-white/5">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">
            Agent catalog
          </p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
            Every role, ready to hire.
          </h2>
          <p className="mt-3 text-base text-brand-ink/70">
            A curated library of virtual employees. Owner-configured, tenant-scoped, audit-logged.
          </p>
        </div>
        <Link
          to="/features"
          className="text-sm text-brand-violet hover:text-brand-violet/80 inline-flex items-center gap-1 self-start md:self-end"
        >
          Browse all features
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-10 -mx-4 md:mx-0">
        <AgentMarqueeCarousel />
      </div>
    </SectionContainer>
  );
}

function FeatureDeepDive() {
  const blocks = [
    {
      icon: ShieldCheck,
      tag: "Multi-tenant isolation",
      title: "Your data never leaves your workspace.",
      body: "Every record is scoped by client_id at the database level, enforced in middleware, and validated at the repository layer. No shared tables, no fuzzy filters, no surprises.",
      bullets: [
        "Row-level tenant scoping on every query",
        "Per-tenant API keys, per-tenant OAuth tokens",
        "Audit log shows every tool call with tenant context",
      ],
    },
    {
      icon: UserCheck,
      tag: "Human-in-the-loop",
      title: "Agents draft. People approve the blast radius.",
      body: "Customer-facing replies, PR merges, refund approvals, outbound emails — all pass through a gated queue where your humans make the final call. Confidence thresholds are configurable per action.",
      bullets: [
        "Per-action approval rules",
        "One-click approve or edit from Slack",
        "Approval SLA tracking with escalation",
      ],
    },
    {
      icon: Plug,
      tag: "Real integrations",
      title: "GitHub, Slack, Gmail, Calendar, Twilio, and more.",
      body: "Agents act through scoped OAuth connections and per-tenant app installations. No sidecar scrapers, no password sharing, no flimsy middleware.",
      bullets: [
        "GitHub App for PR workflows",
        "Gmail and Google Calendar via OAuth",
        "Twilio for voice, Slack for everything",
      ],
    },
  ];

  return (
    <SectionContainer className="border-t border-white/5 bg-white/[0.02]">
      <div className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">
          Built for real work
        </p>
        <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
          Three things we refused to skip.
        </h2>
      </div>

      <div className="mt-16 space-y-20 md:space-y-28">
        {blocks.map((b, i) => {
          const reverse = i % 2 === 1;
          return (
            <div
              key={b.title}
              className={
                "grid lg:grid-cols-2 gap-10 lg:gap-16 items-center " +
                (reverse ? "lg:[&>div:first-child]:order-2" : "")
              }
            >
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1">
                  <b.icon className="h-3.5 w-3.5 text-brand-violet" />
                  <span className="text-xs font-medium text-brand-ink/80">{b.tag}</span>
                </span>
                <h3 className="mt-4 font-display text-3xl md:text-4xl tracking-wide text-brand-ink leading-tight">
                  {b.title}
                </h3>
                <p className="mt-4 text-base text-brand-ink/70 leading-relaxed">{b.body}</p>
                <ul className="mt-6 space-y-2.5">
                  {b.bullets.map((bl) => (
                    <li key={bl} className="flex items-start gap-2.5">
                      <CheckCircle2 className="h-4 w-4 text-brand-violet shrink-0 mt-0.5" />
                      <span className="text-sm text-brand-ink/85">{bl}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <FeatureDiagram kind={b.tag} />
            </div>
          );
        })}
      </div>
    </SectionContainer>
  );
}

function FeatureDiagram({ kind }: { kind: string }) {
  return (
    <div className="relative rounded-2xl border border-white/10 bg-[#0F0F17] p-6 aspect-[4/3]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(124,58,237,0.15),transparent_60%)] rounded-2xl" aria-hidden />
      <div className="relative h-full flex items-center justify-center">
        <div className="w-full space-y-3">
          {kind === "Multi-tenant isolation" && (
            <>
              <DiagramRow label="client_id = FITFAB" color="violet" />
              <DiagramRow label="client_id = ACME" color="cyan" />
              <DiagramRow label="client_id = NOVA" color="violet" />
              <div className="mt-4 text-[10px] font-mono text-brand-ink/40 text-center">
                WHERE client_id = $1 — enforced
              </div>
            </>
          )}
          {kind === "Human-in-the-loop" && (
            <>
              <DiagramRow label="Draft reply (confidence 0.62)" color="orange" />
              <DiagramRow label="Awaiting approval" color="violet" pulse />
              <DiagramRow label="Approved by jo@acme.com" color="cyan" />
              <div className="mt-4 text-[10px] font-mono text-brand-ink/40 text-center">
                approval_queue.action → approved
              </div>
            </>
          )}
          {kind === "Real integrations" && (
            <>
              <DiagramRow label="github.com/acme — installed" color="cyan" />
              <DiagramRow label="slack.com — oauth granted" color="cyan" />
              <DiagramRow label="gmail.com — oauth granted" color="cyan" />
              <div className="mt-4 text-[10px] font-mono text-brand-ink/40 text-center">
                oauth_tokens.scope = read_write
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DiagramRow({
  label,
  color,
  pulse,
}: {
  label: string;
  color: "violet" | "cyan" | "orange";
  pulse?: boolean;
}) {
  const dotColor =
    color === "violet" ? "bg-brand-violet" : color === "cyan" ? "bg-brand-cyan" : "bg-signal-awaiting";
  return (
    <div className="flex items-center gap-3 rounded-lg border border-white/5 bg-white/5 px-3 py-2.5">
      <span
        className={"h-2 w-2 rounded-full shrink-0 " + dotColor + (pulse ? " animate-pulse" : "")}
        aria-hidden
      />
      <span className="text-xs font-mono text-brand-ink/80">{label}</span>
    </div>
  );
}

function PricingPreview() {
  return (
    <SectionContainer className="border-t border-white/5">
      <div className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">Pricing</p>
        <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
          Flat fees. No token math.
        </h2>
        <p className="mt-3 text-base text-brand-ink/70">
          Transparent, per-agent pricing. Cancel anytime.
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {PRICING_TIERS.map((t) => (
          <PricingCard key={t.id} tier={t} />
        ))}
      </div>

      <div className="mt-10 text-center">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1 text-sm text-brand-violet hover:text-brand-violet/80"
        >
          See full feature matrix
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </SectionContainer>
  );
}

function Testimonials() {
  const items = [
    {
      quote:
        "Quote forthcoming — a design partner onboards next month and will share how they deployed three virtual engineers.",
      company: "Design partner",
      context: "Early-access engineering team",
    },
    {
      quote:
        "Quote forthcoming — a mid-market ops team replaced a contractor backlog with a Ticket Responder and Executive Assistant.",
      company: "Design partner",
      context: "Customer operations",
    },
    {
      quote:
        "Quote forthcoming — our first growth customer used the SDR and Ad Campaign Manager to launch three paid channels in a week.",
      company: "Design partner",
      context: "Growth and marketing",
    },
  ];
  return (
    <SectionContainer className="border-t border-white/5 bg-white/[0.02]">
      <div className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">Design partners</p>
        <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
          Real teams. Real work. Real quotes soon.
        </h2>
        <p className="mt-3 text-base text-brand-ink/70">
          We'd rather leave these honest than fabricate them. Quotes will land when our design partners go live.
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {items.map((t, i) => (
          <TestimonialCard key={i} {...t} />
        ))}
      </div>
    </SectionContainer>
  );
}

function Faq() {
  return (
    <SectionContainer className="border-t border-white/5">
      <div className="max-w-3xl mx-auto">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">FAQ</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
            Questions we hear a lot.
          </h2>
        </div>
        <div className="mt-10 space-y-3">
          {HOME_FAQ.map((item, i) => (
            <FaqItem key={i} item={item} />
          ))}
        </div>
      </div>
    </SectionContainer>
  );
}
